import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  calculateIdealGoalWeights,
  selectIdealGoalWeight,
  selectIdealGoalWeights,
  selectIsSubmitting,
  selectRemainingQuestions,
  selectSurveyAnswers,
  setPhysicalDetails
} from "../../redux/survey/macroSurveySlice";
import {Body, Heading4, HeroHeading2, TextStyles} from "../text";
import {Button} from "../buttons/Button";
import {DietGoal, HeightUnit, WeightUnit} from "../../models/fitnessProfile";
import {BodyInput} from "../inputs";
import {getIntrinsicTextWidth} from "../../utils/getIntrinsicTextWidth";
import {UnitsUtils} from "../../utils/unitsUtils";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const MacroSurveyPhysicalDetailsQuestionView: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const answers = useAppSelector(selectSurveyAnswers)
  const idealGoalWeight = useAppSelector(selectIdealGoalWeight)
  const idealGoalWeights = useAppSelector(selectIdealGoalWeights)
  const remainingQuestions = useAppSelector(selectRemainingQuestions)
  const isSubmitting = useAppSelector(selectIsSubmitting)

  const [isValid, setIsValid] = useState<boolean>(false)

  const heightFeetRef = useRef<HTMLInputElement>(null)
  const heightInchesRef = useRef<HTMLInputElement>(null)
  const heightCentimetersRef = useRef<HTMLInputElement>(null)
  const startingWeightRef = useRef<HTMLInputElement>(null)
  const goalWeightRef = useRef<HTMLSelectElement>(null)

  const [heightFeetWidth, setHeightFeetWidth] = useState<number>();
  const [heightInchesWidth, setHeightInchesWidth] = useState<number>();

  const [enteredStartingWeight, setEnteredStartingWeight] = useState<string | undefined>(answers.startingWeight)
  const [enteredHeightFeet, setEnteredHeightFeet] = useState<string | undefined>(answers.heightFeet)
  const [enteredHeightInches, setEnteredHeightInches] = useState<string | undefined>(answers.heightInches)
  const [enteredHeightCentimeters, setEnteredHeightCentimeters] = useState<string | undefined>(answers.heightCentimeters)

  const [selectedGoalWeight, setSelectedGoalWeight] = useState<string | undefined>(answers.goalWeight ?? idealGoalWeight?.toString())
  const [selectedHeightUnit, setSelectedHeightUnit] = useState<HeightUnit>(answers.heightUnit)
  const [selectedWeightUnit, setSelectedWeightUnit] = useState<WeightUnit>(answers.weightUnit)

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Nutrition Survey' }, { 'Survey Question': 'Physical Details' }))
  }, [])

  useEffect(() => {
    if (goalWeightRef.current && idealGoalWeight) {
      goalWeightRef.current.value = idealGoalWeight.toString()
    }
  }, [idealGoalWeight])

  useEffect(() => {
    let valid: boolean;
    const goalWeight = goalWeightRef.current?.value
    if (selectedHeightUnit === HeightUnit.imperial) {
      valid = !!(
        enteredHeightFeet
        && enteredHeightInches
        && enteredStartingWeight
        && goalWeight
      );
    } else {
      valid = !!(
        enteredHeightCentimeters
        && enteredStartingWeight
        && goalWeight
      );
    }

    setIsValid(valid)
  }, [idealGoalWeight, selectedGoalWeight, selectedHeightUnit, enteredStartingWeight, enteredHeightFeet, enteredHeightInches, enteredHeightCentimeters])

  useEffect(() => {
    let valid: boolean;
    if (selectedHeightUnit === HeightUnit.imperial) {
      valid = !!(
        enteredHeightFeet
        && enteredHeightInches
        && enteredStartingWeight
      );
    } else {
      valid = !!(
        enteredHeightCentimeters
        && enteredStartingWeight
      );
    }

    const heightFeet = enteredHeightFeet
    const heightInches = enteredHeightInches
    const heightCentimeters = enteredHeightCentimeters
    const startingWeight = enteredStartingWeight

    if (valid) {
      dispatch(calculateIdealGoalWeights({
        heightFeet,
        heightInches,
        heightCentimeters,
        startingWeight,
        goalWeight: selectedGoalWeight,
        heightUnit: selectedHeightUnit,
        weightUnit: selectedWeightUnit
      }))
    }
  }, [dispatch, selectedGoalWeight, selectedWeightUnit, selectedHeightUnit, enteredHeightFeet, enteredHeightInches, enteredHeightCentimeters, enteredStartingWeight])


  const handleSubmit = () => {
    const heightFeet = heightFeetRef.current?.value
    const heightInches = heightInchesRef.current?.value
    const heightCentimeters = heightCentimetersRef.current?.value
    const startingWeight = startingWeightRef.current?.value
    const goalWeight = selectedGoalWeight ?? goalWeightRef.current?.value

    dispatch(setPhysicalDetails({
      heightFeet,
      heightInches,
      heightCentimeters,
      startingWeight,
      goalWeight,
      heightUnit: selectedHeightUnit,
      weightUnit: selectedWeightUnit
    }))
  }

  return <div className="flex flex-col space-y-8 lg:space-y-10 items-center w-full px-6">
    <HeroHeading2 className="text-center mx-8">{t('macro_survey:macro_survey_header_title_physical_details')}</HeroHeading2>

    <div className="flex flex-col w-full space-y-5">
      <div>
        <Heading4 className="mb-2">{t('macro_survey:macro_survey_title_your_height')}</Heading4>

        <div className="grid grid-cols-17 gap-3">
          <div className={`relative col-span-6 ${selectedHeightUnit === HeightUnit.imperial ? '' : 'hidden'}`}>
            <BodyInput
              type="text"
              ref={heightFeetRef}
              defaultValue={answers.heightFeet}
              onChange={(e) => {
                const text = e.target.value.replace(/[^\d]/,'');
                const value = parseInt(text)


                if((!value || isNaN(value) || value < 1) && heightFeetRef.current) {
                  heightFeetRef.current.value = ''
                  setHeightFeetWidth(undefined)
                } else {
                  setHeightFeetWidth(getIntrinsicTextWidth(heightFeetRef))
                  setEnteredHeightFeet(e.target.value)
                  heightInchesRef.current?.focus()
                }
              }}
              placeholder={ t('macro_survey:macro_survey_unit_title_ft') }
            />

            { heightFeetWidth && <Body className="absolute ml-3 lg:ml-4 top-1/2 transform -translate-y-1/2" style={{left: heightFeetWidth + 1}}>&apos;</Body>}
          </div>

          <div className={`relative col-span-6 ${selectedHeightUnit === HeightUnit.imperial ? '' : 'hidden'}`}>
            <BodyInput
              type="text"
              ref={heightInchesRef}
              defaultValue={answers.heightInches}
              onChange={(e) => {
                const text = e.target.value.replace(/[^\d]/,'');
                const value = parseInt(text)

                if(((!value && value !== 0) || isNaN(value) || value < 0) && heightInchesRef.current) {
                  heightInchesRef.current.value = ''
                  setHeightInchesWidth(undefined)
                } else {
                  setHeightInchesWidth(getIntrinsicTextWidth(heightInchesRef))
                  setEnteredHeightInches(e.target.value)

                  if (value == 1 && text.length >= 2) {
                    startingWeightRef.current?.focus()
                  } else if (value != 1) {
                    startingWeightRef.current?.focus()
                  }
                }
              }}
              placeholder={ t('macro_survey:macro_survey_unit_title_in') }
            />

            { heightInchesWidth && <Body className="absolute ml-3 lg:ml-4 top-1/2 transform -translate-y-1/2" style={{left: heightInchesWidth + 1}}>&quot;</Body>}
          </div>

          <div className={`relative col-span-12 ${selectedHeightUnit === HeightUnit.metric ? '' : 'hidden'}`}>
            <BodyInput
              type="text"
              ref={heightCentimetersRef}
              defaultValue={answers.heightCentimeters}
              onChange={(e) => {
                const text = e.target.value.replace(/[^\d]/,'');
                const value = parseInt(text)

                if ((!value || isNaN(value) || value < 1) && heightCentimetersRef.current) {
                  heightCentimetersRef.current.value = ''
                } else if ((value && !isNaN(value) && value >= 1 && text.length >= 3))  {
                  setEnteredHeightCentimeters(e.target.value)
                  startingWeightRef.current?.focus()
                }
              }}
              placeholder={ t('macro_survey:macro_survey_unit_title_cm') }
            />
          </div>

          <div className="col-span-5">
            <select
              id="height_unit"
              name="height_unit"
              onClick={e => {
                e.stopPropagation()
              }}
              defaultValue={answers.heightUnit}
              onChange={e => {
                setSelectedHeightUnit(e.target.value as HeightUnit)
              }}
              className={`bg-fg-gray-surface-light w-full h-full hover:bg-fg-gray-surface-dark border-0 cursor-pointer focus:ring-0 rounded-lg py-2.5 lg:py-3 flex flex-row items-center ${TextStyles.Body}`}
            >
              <option key={HeightUnit.imperial} value={HeightUnit.imperial}>
                {t('macro_survey:macro_survey_unit_title_ft')}/{t('macro_survey:macro_survey_unit_title_in')}
              </option>
              <option key={HeightUnit.metric} value={HeightUnit.metric}>
                {t('macro_survey:macro_survey_unit_title_cm')}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <div>
          <Heading4 className="mb-2">{t('macro_survey:macro_survey_title_your_current_weight')}</Heading4>

          <div className="grid grid-cols-17 gap-3">
            <div className="col-span-12">
              <BodyInput
                type="text"
                ref={startingWeightRef}
                defaultValue={answers.startingWeight}
                onChange={(e) => {
                  const text = e.target.value.replace(/[^\d]/,'');
                  const value = parseInt(text)

                  if ((!value || isNaN(value) || value < 1) && startingWeightRef.current) {
                    startingWeightRef.current.value = ''
                  } else if ((value && !isNaN(value) && value >= 1 && text.length >= 2))  {
                    setEnteredStartingWeight(e.target.value)
                    // startingWeightRef.current?.focus()
                  }
                }}
                placeholder={
                  t('macro_survey:macro_survey_title_your_weight') + ` (${selectedWeightUnit === WeightUnit.imperial ? t('macro_survey:macro_survey_unit_title_lbs') : t('macro_survey:macro_survey_unit_title_kg')})`
                }
              />
            </div>

            <div className="col-span-5">
              <select
                id="weight_unit"
                name="weight_unit"
                onClick={e => {
                  e.stopPropagation()
                }}
                defaultValue={answers.weightUnit}
                onChange={e => {
                  setSelectedWeightUnit(e.target.value as WeightUnit)
                }}
                className={`bg-fg-gray-surface-light w-full h-full hover:bg-fg-gray-surface-dark border-0 cursor-pointer focus:ring-0 rounded-lg py-2.5 lg:py-3 flex flex-row items-center ${TextStyles.Body}`}
              >
                <option key={WeightUnit.imperial} value={WeightUnit.imperial}>
                  {t('macro_survey:macro_survey_unit_title_lbs')}
                </option>
                <option key={WeightUnit.metric} value={WeightUnit.metric}>
                  {t('macro_survey:macro_survey_unit_title_kg')}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className={`${answers.goal === DietGoal.maintenance ? 'hidden': ''}`}>
        <Heading4 className="mb-2">{t('macro_survey:macro_survey_title_your_goal_weight')}</Heading4>
        <select
          id="goal_weight"
          name="goal_weight"
          ref={goalWeightRef}
          onClick={e => {
            e.stopPropagation()
          }}
          defaultValue={answers.goalWeight ?? idealGoalWeight?.toString()}
          onChange={e => {
            setSelectedGoalWeight(e.target.value as string)
          }}
          className={`bg-fg-gray-surface-light w-full h-full hover:bg-fg-gray-surface-dark border-0 cursor-pointer focus:ring-0 rounded-lg py-2.5 lg:py-3 flex flex-row items-center ${TextStyles.Body}`}
        >
          {
            idealGoalWeights
              .map(i => {
                return <option key={i} value={i}>
                  {selectedWeightUnit === WeightUnit.imperial
                    ? `${i} ${t('macro_survey:macro_survey_unit_title_lbs')}`
                    : `${UnitsUtils.convertWeight(i, WeightUnit.imperial, WeightUnit.metric)?.toFixed(1)} ${t('macro_survey:macro_survey_unit_title_kg')}`
                  }
                </option>
              })
          }
        </select>
      </div>
    </div>

    <div className="w-full md:justify-center md:flex">
      <Button
        isDisabled={!isValid}
        isLoading={isSubmitting}
        size="lg"
        isFullWidth={true}
        onClick={() => {
          handleSubmit()
        }}
      >
        {remainingQuestions.length ? t('common:common_title_continue') : t('common:common_title_submit')}
      </Button>
    </div>
  </div>
}